import type { NodeTreeBuilder } from '@fullstory/shared/node_decoder';
import type { FsDocument } from './fsdocument';
import { FsShadowRootLike } from './fsdocumentorshadowroot';
import type { FsNode } from './fsnode';

/**
 * A {@link NodeTreeBuilder} that constructs trees of {@link FSNode} objects
 * within an {@link FsDocument}.
 */
export class FsNodeTreeBuilder implements NodeTreeBuilder<FsNode> {
  constructor(private readonly _ownerDoc: FsDocument) {}

  createNode(tag: string): FsNode {
    return this._ownerDoc.createNode(tag);
  }

  getParentNode(node: FsNode): FsNode | null {
    return node.parent;
  }

  insertChildNode(parent: FsNode, child: FsNode, before: FsNode | null): void {
    parent.insert(child, before);
  }

  setNodeShadow(node: FsNode, shadow: FsNode): void {
    if (!(shadow instanceof FsShadowRootLike)) {
      console.warn(`setNodeShadow: Wrong node type for shadow node ${node}`);
      return;
    }
    node.setShadow(shadow);
  }

  setNodeText(node: FsNode, text: string): void {
    if (!node.isText()) {
      console.warn(`setNodeText: Wrong node type for target node ${node}`);
      return;
    }

    // Apply text corpus replacement if available
    const textCorpus = this._ownerDoc.ctx.page.textCorpus;
    const key = `${text}`.trim();
    const replacedText = textCorpus && textCorpus[key] ? textCorpus[key] : text;

    node.setText(replacedText);
  }

  setNodeAttr(node: FsNode, name: string, requestedValue: string): void {
    // Apply text corpus replacement if available
    const textCorpus = this._ownerDoc.ctx.page.textCorpus;
    const key = `${requestedValue}`.trim();
    if (
      textCorpus &&
      name === 'value' &&
      (node.tag === 'input' || node.tag === 'textarea' || node.tag === 'select')
    ) {
      const replacedValue = textCorpus[key] ?? requestedValue;
      node.setAttr(name, replacedValue);
    } else {
      node.setAttr(name, requestedValue);
    }
  }
}
